import { DEFAULT_PAGE_SIZE, SortDirection } from '@/components/Table/types';
import { HttpClient } from '@/core/http/http-client';
import {
  mapLabourTimeAutomatedList,
  mapLastRunSuccessRateList,
  mapOverallRunsSuccessRateList,
  mapTotalReturnList,
} from '@/shared/mappers/kpi-mapper';
import {
  ApiResponse,
  DataWithPagination,
  ExceptionType,
  LabourTimeAutomatedDetailsJson,
  LastRunSuccessRateDetailsJson,
  OverallRunsSuccessRateDetailsJson,
  Page,
  TotalReturnDetailsJson,
} from '@/shared/types';
import { DateUtility } from '@/utils/date.utils';
import { DateRange } from 'react-day-picker';
import {
  Process,
  ProcessBaseInfo,
  ProcessDetails,
  ProcessItem,
  UserProcess,
} from '../domain';
import {
  mapProcessDetails,
  mapProcessesBaseInfo,
  mapProcessesPage,
  mapProcessItemsPage,
  processAutomationPerformanceMapper,
  userProcessesPageMapper,
} from '../mappers';
import {
  ApiResponseProcess,
  ApiResponseProcesses,
  AssociateWorkflowToProcess,
  CreateProcessJson,
  DisassociateWorkflowFromProcess,
  EditProcessJson,
  ProcessAutomationsPerformanceJson,
  ProcessBaseInfoJson,
  ProcessDetailsJson,
  ProcessItemJson,
  ProcessJson,
  UserProcessJson,
} from '../types';
import { AttachUserProcessModel } from '../types/attach-user-entity-model';
import { ProcessRepository } from './process.repository';

export class HttpProcessRepository extends ProcessRepository {
  constructor(private readonly client: HttpClient) {
    super();
  }

  async getAllProcesses(
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ): Promise<DataWithPagination<ProcessItem[]>> {
    const response = await this.client.get<
      ApiResponseProcesses<Page<ProcessItemJson>>
    >({
      url: '/me/processes',
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return mapProcessItemsPage(response.data.processes);
  }

  async createProcess(data: CreateProcessJson) {
    await this.client.post({ body: data });
  }

  async attachUserProcess(data: AttachUserProcessModel): Promise<void> {
    return await this.client.post({
      body: data,
      url: `associate-user`,
    });
  }

  async getProcess(id: number): Promise<ProcessDetails> {
    const process = await this.client.get<
      ApiResponseProcess<ProcessDetailsJson>
    >({
      url: `${id}`,
    });

    return mapProcessDetails(process.data.process);
  }

  async enableProcess(id: number): Promise<ApiResponse<unknown>> {
    return await this.client.post({
      url: `${id}/enable`,
    });
  }

  async disableProcess(id: number): Promise<ApiResponse<unknown>> {
    return await this.client.post({
      url: `${id}/disable`,
    });
  }

  async updateProcess(data: EditProcessJson): Promise<ApiResponse<unknown>> {
    return await this.client.put<ApiResponse<unknown>>(`${data.id}`, data);
  }

  async getProcessesByBusinessArea(
    businessAreaId: number,
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ): Promise<DataWithPagination<Process[]>> {
    const response = await this.client.get<
      ApiResponseProcesses<Page<ProcessJson>>
    >({
      url: `/business-areas/${businessAreaId}/processes`,
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return mapProcessesPage(response.data.processes);
  }

  async getAutomationsPerformanceById(
    processId: number,
    dateRange?: DateRange,
  ) {
    const response = await this.client.get<
      ApiResponse<ProcessAutomationsPerformanceJson>
    >({
      url: `${processId}/automations-performance`,
      query: {
        ...DateUtility.extractDateStringQueryParamsFromRange(dateRange),
      },
    });

    return processAutomationPerformanceMapper(response.data);
  }

  async getProcessesBaseInfoByBusinessArea(
    businessAreaId: number,
  ): Promise<ProcessBaseInfo[]> {
    const processes = await this.client.get<
      ApiResponseProcesses<ProcessBaseInfoJson[]>
    >({
      url: `/business-areas/${businessAreaId}/processes/base-info`,
    });

    return mapProcessesBaseInfo(processes.data.processes);
  }

  async getProcessesByCompany(
    companyId: number,
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ): Promise<DataWithPagination<Process[]>> {
    const response = await this.client.get<
      ApiResponseProcesses<Page<ProcessJson>>
    >({
      url: `/companies/${companyId}/processes`,
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return mapProcessesPage(response.data.processes);
  }

  async getProcessesByOrganization(
    organizationId: number,
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ): Promise<DataWithPagination<Process[]>> {
    const response = await this.client.get<
      ApiResponseProcesses<Page<ProcessJson>>
    >({
      url: `/organizations/${organizationId}/processes`,
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return mapProcessesPage(response.data.processes);
  }

  async getProcessesByUser(
    userId: number,
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ): Promise<DataWithPagination<UserProcess[]>> {
    const response = await this.client.get<
      ApiResponseProcesses<Page<UserProcessJson>>
    >({
      url: `/users/${userId}/processes`,
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return userProcessesPageMapper(response.data.processes);
  }

  async associateWorkflowToProcess(
    data: AssociateWorkflowToProcess,
  ): Promise<ApiResponse<unknown>> {
    return await this.client.post({
      url: `associate-workflow`,
      body: data,
    });
  }

  async disassociateWorkflowFromProcess(
    data: DisassociateWorkflowFromProcess,
  ): Promise<ApiResponse<unknown>> {
    return await this.client.post({
      url: `disassociate-workflow`,
      body: data,
    });
  }

  async deleteProcess(id: number): Promise<ApiResponse<unknown>> {
    return await this.client.delete(`${id}`);
  }

  async getProcessLabourTimeAutomatedById(
    processId: number,
    dateRange?: DateRange,
    page?: number,
    filter?: string,
  ) {
    const response = await this.client.get<
      ApiResponse<LabourTimeAutomatedDetailsJson>
    >({
      url: `${processId}/labour-time-automated`,
      query: {
        page,
        filter,
        ...DateUtility.extractDateStringQueryParamsFromRange(dateRange),
      },
    });

    return mapLabourTimeAutomatedList(
      response.data.executions,
      response.data.totals,
    );
  }

  async getProcessTotalReturnById(
    processId: number,
    dateRange?: DateRange,
    page?: number,
    filter?: string,
  ) {
    const response = await this.client.get<ApiResponse<TotalReturnDetailsJson>>(
      {
        url: `${processId}/total-return`,
        query: {
          page,
          filter,
          ...DateUtility.extractDateStringQueryParamsFromRange(dateRange),
        },
      },
    );

    return mapTotalReturnList(response.data.executions, response.data.totals);
  }

  async getProcessLastRunById(
    processId: number,
    page?: number,
    filter?: string,
    result?: boolean,
    exceptionType?: ExceptionType,
  ) {
    const response = await this.client.get<
      ApiResponse<LastRunSuccessRateDetailsJson>
    >({
      url: `${processId}/last-run`,
      query: {
        page,
        filter,
        result,
        exceptionType,
      },
    });

    return mapLastRunSuccessRateList(response.data.executions);
  }

  async getProcessOverallRunsById(
    processId: number,
    dateRange?: DateRange,
    page?: number,
    filter?: string,
    result?: boolean,
    exceptionType?: ExceptionType,
  ) {
    const response = await this.client.get<
      ApiResponse<OverallRunsSuccessRateDetailsJson>
    >({
      url: `${processId}/overall-runs`,
      query: {
        page,
        filter,
        result,
        ...DateUtility.extractDateStringQueryParamsFromRange(dateRange),
        exceptionType,
      },
    });

    return mapOverallRunsSuccessRateList(response.data.executions);
  }
}
